import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "../components/site/Seo";
import { PageHeader } from "../components/site/Section";
import { Reveal } from "../components/site/Reveal";
import { INDUSTRIES } from "../data/site";
import useHashScroll from "../hooks/useHashScroll";

const DETAIL = {
  "Government Agencies": "Federal, state, and local staffing aligned to procurement timelines, security requirements, and public accountability.",
  "Defense Contractors": "Cleared and clearable talent for prime and subcontractor programs, from engineering to program management.",
  "Healthcare Organizations": "Clinical, allied health, and administrative staffing for hospitals, systems, and payers.",
  "Professional Services": "Finance, legal, consulting, and technology talent for firms scaling delivery capacity.",
  "Logistics Companies": "Operations, supply chain, and skilled labor to keep distribution networks running.",
  "Commercial Enterprises": "Growth-stage and enterprise hiring across corporate functions and transformation initiatives.",
};

export default function Industries() {
  useHashScroll();
  return (
    <div data-testid="industries-page">
      <Seo title="Industries" description="VectorBridge Group serves government, defense, healthcare, professional services, logistics, and commercial enterprises." />
      <PageHeader
        label="I/01"
        kicker="Industries"
        title="Six sectors. One standard of rigor."
        intro="We tailor sourcing, screening, and compliance to the realities of each sector we serve — never a one-size-fits-all model."
      />

      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 py-20 sm:px-8">
          <div className="space-y-px bg-border">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.name} id={ind.slug} delay={(i % 2) * 0.05} className="scroll-mt-28">
                <div className="group grid items-stretch gap-0 bg-background md:grid-cols-12">
                  <div className="relative aspect-[16/9] overflow-hidden md:col-span-4 md:aspect-auto">
                    <img src={ind.img} alt={ind.name} className="absolute inset-0 h-full w-full object-cover opacity-50 grayscale transition-all duration-700 group-hover:scale-105 group-hover:opacity-80 group-hover:grayscale-0" />
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent to-background/60" />
                  </div>
                  <div className="flex flex-col justify-center gap-3 p-8 md:col-span-8 md:p-12">
                    <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-brand">{ind.tag}</div>
                    <h3 className="font-display text-3xl tracking-tight sm:text-4xl">{ind.name}</h3>
                    <p className="max-w-2xl text-muted-foreground">{DETAIL[ind.name]}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-border bg-secondary">
        <div className="mx-auto flex max-w-[1400px] flex-col items-start justify-between gap-6 px-5 py-16 sm:flex-row sm:items-center sm:px-8">
          <h2 className="font-display text-3xl font-light tracking-tighter sm:text-4xl">Don't see your sector? We adapt.</h2>
          <Link to="/contact" data-testid="industries-cta" className="group inline-flex items-center gap-2 bg-brand px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] text-white transition-colors hover:bg-navy">
            Talk to us <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>
    </div>
  );
}
